import { createSlice } from '@reduxjs/toolkit';
import missionService from '../../utils/missionService';

const initialState = {
  missions: [],
  userMissions: [],
  selectedMission: null,
  missionApplications: [],
  favorites: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    discipline: '',
    location: '',
    type: '',
    rateMin: 0,
    rateMax: 1000,
    dateFrom: '',
    dateTo: '',
    distance: 50
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  sortBy: 'relevance'
};

const transformMission = (mission) => {
  if (!mission) return mission;
  const id = mission._id || mission.id;
  const title = mission.title;
  const description = mission.description;
  const discipline = mission.sport || mission.discipline;
  const location = mission.location;
  const schedule = mission.schedule || {};
  const date = schedule.startDate || mission.date;
  const compensation = mission.compensation || {};
  const rateAmount = compensation.amount;
  const rateCurrency = compensation.currency;
  const rateType = compensation.type; // 'paid' | 'volunteer' | 'exchange'
  const rate = rateType === 'paid' && rateAmount
    ? `${rateAmount} ${rateCurrency || 'EUR'}`
    : rateType === 'volunteer'
      ? 'Volontaire'
      : rateAmount ? `${rateAmount} ${rateCurrency || 'EUR'}` : undefined;
  const mediaImages = mission.images || [];
  const image = mission.image || mediaImages[0]?.url;
  const organization = mission.organization;
  const organizationLogo = organization?.logo?.url || organization?.logo;
  const organizationId = organization && typeof organization === 'object' ? (organization._id || organization.id) : undefined;
  const type = mission.category || mission.type;
  const status = mission.status;
  const applicationCount = mission.applicationCount ?? mission.stats?.applications ?? (Array.isArray(mission.applications) ? mission.applications.length : 0);
  const daysUntilStart = mission.daysUntilStart ?? (schedule.startDate ? Math.ceil((new Date(schedule.startDate) - new Date()) / (1000 * 60 * 60 * 24)) : null);
  const averageRating = mission.averageRating ?? 0;
  const acceptedParticipants = mission.acceptedParticipants ?? (Array.isArray(mission.applications) ? mission.applications.filter(a => a.status === 'accepted').length : 0);
  const requiredParticipants = mission.participants?.required ?? mission.participants?.max;
  const successRate = requiredParticipants ? Math.round(((acceptedParticipants || 0) / requiredParticipants) * 100) : 0;

  return {
    id,
    title,
    description,
    discipline,
    location,
    date,
    rate,
    image,
    organization,
    organizationLogo,
    organizationId,
    type,
    status,
    applicationCount,
    daysUntilStart,
    averageRating,
    successRate
  };
};

const missionsSlice = createSlice({
  name: 'missions',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setMissions: (state, action) => {
      const rawData = action.payload?.data || action.payload?.missions || action.payload || [];
      state.missions = Array.isArray(rawData) ? rawData.map(transformMission) : [];
      const pg = action.payload?.pagination;
      if (pg) {
        state.pagination = {
          currentPage: pg.page ?? state.pagination.currentPage,
          totalPages: pg.totalPages ?? state.pagination.totalPages,
          totalItems: action.payload?.total ?? state.pagination.totalItems,
          itemsPerPage: pg.limit ?? state.pagination.itemsPerPage
        };
      }
      state.loading = false;
      state.error = null;
    },
    setUserMissions: (state, action) => {
      state.userMissions = action.payload.missions || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setSelectedMission: (state, action) => {
      state.selectedMission = transformMission(action.payload);
      state.loading = false;
      state.error = null;
    },
    setMissionApplications: (state, action) => {
      state.missionApplications = action.payload;
      state.loading = false;
      state.error = null;
    },
    addMission: (state, action) => {
      state.missions.unshift(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateMission: (state, action) => {
      const index = state.missions.findIndex(mission => mission._id === action.payload._id);
      if (index !== -1) {
        state.missions[index] = action.payload;
      }
      if (state.selectedMission && state.selectedMission._id === action.payload._id) {
        state.selectedMission = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeMission: (state, action) => {
      state.missions = state.missions.filter(mission => mission._id !== action.payload);
      state.userMissions = state.userMissions.filter(mission => mission._id !== action.payload);
      if (state.selectedMission && state.selectedMission._id === action.payload) {
        state.selectedMission = null;
      }
      state.loading = false;
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        discipline: '',
        location: '',
        type: '',
        rateMin: 0,
        rateMax: 1000,
        dateFrom: '',
        dateTo: '',
        distance: 50
      };
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    toggleFavorite: (state, action) => {
      const missionId = String(action.payload);
      const index = state.favorites.findIndex(id => id === missionId);
      if (index !== -1) {
        state.favorites.splice(index, 1);
      } else {
        state.favorites.push(missionId);
      }
    },
    setFavorites: (state, action) => {
      state.favorites = action.payload;
    }
  }
});

// Actions asynchrones
export const fetchMissions = (params = {}) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    // Build query params from current state + optional overrides
    const buildParams = (state) => {
      const { filters, pagination, sortBy } = state.missions;
      const query = {};

      // Text search
      if (filters.search) query.search = filters.search;

      // Basic filters mapped to backend fields (best-effort, non-breaking)
      if (filters.discipline) query.sport = filters.discipline;
      if (filters.type) query.category = filters.type;

      // Pagination
      query.page = pagination.currentPage;
      query.limit = pagination.itemsPerPage;

      // Sorting
      if (sortBy === 'date') query.sort = 'schedule.startDate';
      else if (sortBy === 'rate-asc') query.sort = 'compensation.amount';
      else if (sortBy === 'rate-desc') query.sort = '-compensation.amount';
      // 'relevance' uses backend default

      // Note: Advanced range filters (rate/date) omitted to avoid backend mismatch
      // They can be added once the UI collects canonical fields

      // Allow overriding via params
      return { ...query, ...params };
    };

    // Retrieve state via thunk arg by adding getState param
    const response = await missionService.getMissions(buildParams(getState()));
    dispatch(setMissions(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchMissionById = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const mission = await missionService.getMissionById(id);
    dispatch(setSelectedMission(mission));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const createMission = (missionData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const newMission = await missionService.createMission(missionData);
    dispatch(addMission(newMission));
    return newMission;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateMissionById = (id, missionData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedMission = await missionService.updateMission(id, missionData);
    dispatch(updateMission(updatedMission));
    return updatedMission;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteMissionById = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await missionService.deleteMission(id);
    dispatch(removeMission(id));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const applyToMission = (id, applicationData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const application = await missionService.applyToMission(id, applicationData);
    // Optionnel : rafraîchir la mission pour mettre à jour le nombre de candidatures
    dispatch(fetchMissionById(id));
    return application;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchMissionApplications = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const applications = await missionService.getMissionApplications(id);
    dispatch(setMissionApplications(applications));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const acceptApplication = (missionId, applicationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await missionService.acceptApplication(missionId, applicationId);
    // Rafraîchir les candidatures
    dispatch(fetchMissionApplications(missionId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const rejectApplication = (missionId, applicationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await missionService.rejectApplication(missionId, applicationId);
    // Rafraîchir les candidatures
    dispatch(fetchMissionApplications(missionId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const uploadMissionMedia = (id, formData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await missionService.uploadMissionMedia(id, formData);
    // Rafraîchir la mission pour afficher les nouveaux médias
    dispatch(fetchMissionById(id));
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const searchMissionsByLocation = (params) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await missionService.searchByLocation(params);
    dispatch(setMissions(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchUserMissions = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await missionService.getUserMissions(params);
    dispatch(setUserMissions(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const updateMissionStatus = (id, status) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedMission = await missionService.updateMissionStatus(id, status);
    dispatch(updateMission(updatedMission));
    return updatedMission;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const addMissionReview = (id, reviewData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const review = await missionService.addMissionReview(id, reviewData);
    // Rafraîchir la mission pour afficher le nouvel avis
    dispatch(fetchMissionById(id));
    return review;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const searchMissions = (query, params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await missionService.searchMissions(query, params);
    dispatch(setMissions(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

// Sélecteurs
export const selectMissions = (state) => state.missions.missions;
export const selectUserMissions = (state) => state.missions.userMissions;
export const selectSelectedMission = (state) => state.missions.selectedMission;
export const selectMissionApplications = (state) => state.missions.missionApplications;
export const selectMissionsLoading = (state) => state.missions.loading;
export const selectMissionsError = (state) => state.missions.error;
export const selectMissionsFilters = (state) => state.missions.filters;
export const selectMissionsPagination = (state) => state.missions.pagination;
export const selectMissionsSortBy = (state) => state.missions.sortBy;
export const selectFavorites = (state) => state.missions.favorites;

export const selectMissionById = (state, missionId) => {
  return state.missions.missions.find(mission => (mission.id || mission._id) === missionId);
};

export const selectFilteredMissions = (state) => {
  const { missions, filters } = state.missions;
  
  return missions.filter(mission => {
    // Filtrage par recherche textuelle
    if (filters.search && !mission.title.toLowerCase().includes(filters.search.toLowerCase()) &&
        !mission.description.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    // Filtrage par discipline
    if (filters.discipline && mission.discipline !== filters.discipline) {
      return false;
    }
    
    // Filtrage par localisation
    if (filters.location && !mission.location.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    // Filtrage par type
    if (filters.type && mission.type !== filters.type) {
      return false;
    }
    
    // Filtrage par tarif
    const rate = parseFloat(mission.rate?.replace(/[^\d.]/g, '') || 0);
    if (rate < filters.rateMin || rate > filters.rateMax) {
      return false;
    }
    
    // Filtrage par date
    if (filters.dateFrom && new Date(mission.date) < new Date(filters.dateFrom)) {
      return false;
    }
    
    if (filters.dateTo && new Date(mission.date) > new Date(filters.dateTo)) {
      return false;
    }
    
    return true;
  });
};

export const selectFavoriteMissions = (state) => {
  const { missions, favorites } = state.missions;
  return missions.filter(mission => favorites.includes(mission._id));
};

export const {
  setLoading,
  setError,
  clearError,
  setMissions,
  setUserMissions,
  setSelectedMission,
  setMissionApplications,
  addMission,
  updateMission,
  removeMission,
  setFilters,
  clearFilters,
  setSortBy,
  setPagination,
  toggleFavorite,
  setFavorites
} = missionsSlice.actions;

export default missionsSlice.reducer;